import React, { lazy, Suspense, useCallback, useRef } from 'react';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@leadsnavi/ui-kit';
import { Minimize2, Paperclip, Send, RotateCcw } from 'lucide-react';
import { useMailEditorStore } from '@/stores/mailEditorStore';
import { MailEditorRef } from '../index';
import { useParams } from 'react-router-dom';
// import { PagePath } from '@/routes/paths';
import PageLoading from '@/common/base/PageLoading';
import { ModalComponentProps } from '@/components/moda-lInvoker';
import SubjectInput from '../SubjectInput';
import { useMailEditorActions } from '@/hooks/useMailEditorActions';
import { useAutoSaveDraft } from '@/hooks/useAutoSaveDraft';
import { useContentEditingStatus } from '@/hooks/useContentEditingStatus';
// import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import { useEditorContentSync } from '@/hooks/useEditorContentSync';
// import mixpanel from 'mixpanel-browser';
// import DraftsDialog from '@/components/drafts-dialog';
// import { getDraftListPost } from '@/services/swagger/easourcing/WriteService';
// 懒加载邮件编辑器组件
const LazyMailEditor = lazy(() => import('../index'));

interface GlobalMailEditorModalProps extends ModalComponentProps {
  customToolbarPlugin?: React.ReactElement;
}

/**
 * Save 按钮组件
 */
export const SaveButton: React.FC<{ editorRef: React.RefObject<MailEditorRef>; dialogSubject: string }> = ({
  editorRef,
  dialogSubject,
}) => {
  const { handleSave, saveLoading } = useMailEditorActions(editorRef, dialogSubject);
  const { content } = useMailEditorStore();

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!content || !dialogSubject}
      loading={saveLoading}
      onClick={handleSave}
    >
      <RotateCcw size={16} />
      Reset
    </Button>
  );
};

/**
 * 编辑器工具栏插件组件
 * 封装发送按钮和相关逻辑
 */
const EditorPlugin: React.FC<{
  editorRef: React.RefObject<MailEditorRef>;
  dialogSubject: string;
  onCancel?: () => void;
}> = ({ editorRef, dialogSubject, onCancel }) => {
  const { content } = useMailEditorStore();
  const { handleSend, sendLoading } = useMailEditorActions(editorRef, dialogSubject);

  const handleSendMail = useCallback(async () => {
    await handleSend(() => {
      onCancel?.();
    });
  }, [handleSend, onCancel]);

  return (
    <div className="flex-1 flex items-center justify-end gap-2 h-8">
      {/* <SaveButton editorRef={editorRef} dialogSubject={dialogSubject} /> */}
      <Button
        disabled={!dialogSubject && !content}
        size="sm"
        loading={sendLoading}
        onClick={handleSendMail}
        id="contactsdetails_send_click"
      >
        <Send size={16} />
        Send
      </Button>
    </div>
  );
};

/**
 * 全局邮件编辑器弹窗组件
 * 使用全局 Modal 系统，相对于 body 定位，解决右键菜单定位问题
 */
const GlobalMailEditorModal: React.FC<GlobalMailEditorModalProps> = ({ open, onCancel }) => {
  const {
    editorMode,
    setEditorMode,
    setDialogVisible,
    setContent,
    setAttachments,
    attachments,
    subject,
    setSubject,
    draftId,
  } = useMailEditorStore();

  const editorRef = useRef<MailEditorRef>(null);
  // const navigate = useNavigate();
  const { contactId } = useParams<{ contactId: string }>();
  // const [dialogSubject, setDialogSubject] = useState(subject);
  // const [subjectFocus, setSubjectFocus] = useState(false);
  // const [draftCount, setDraftCount] = useState(0);

  // 使用正文编辑状态跟踪 hook（基于 onEditorChange 事件）
  const { isContentEditing, notifyContentEditing } = useContentEditingStatus(3000);

  // 使用自动保存 hook
  useAutoSaveDraft({
    editorRef,
    subject: subject,
    draftId,
    contactId,
    isEditing: isContentEditing,
    enabled: open,
  });

  // 获取草稿数量
  // const fetchDraftCount = useCallback(async () => {
  //   if (!contactId) return;

  //   try {
  //     const response = await getDraftListPost({
  //       contactId,
  //       pageSize: 0,
  //     });
  //     setDraftCount(response?.data?.totalSize || 0);
  //   } catch (error) {
  //     console.error('获取草稿数量失败:', error);
  //     // 失败时不更新数量，保持当前显示
  //   }
  // }, [contactId]);

  // 使用 useAutoRefresh 进行定时刷新（每10秒）
  // useAutoRefresh(fetchDraftCount, {
  //   baseInterval: 10000, // 10秒
  //   enabled: !!contactId && open,
  //   isVisible: open,
  // });

  // 组件挂载或 contactId 变化时立即获取一次
  // useEffect(() => {
  //   if (contactId && open) {
  //     fetchDraftCount();
  //   } else {
  //     setDraftCount(0);
  //   }
  // }, [contactId, open, fetchDraftCount]);

  // 使用统一的编辑器内容同步 hook
  const { onEditCreated } = useEditorContentSync({
    editorRef,
    enabledMode: 'dialog',
    open,
    onApply: ({ subject }) => {
      setSubject(subject);
    },
  });

  // 处理草稿箱按钮点击
  // const handleDraftsClick = () => {
  //   if (!contactId) return;

  //   mixpanel.track('contactsdetails_drafts_click');

  //   ModalService.open(DraftsDialog, {
  //     contactId: contactId,
  //     onCancel: () => {
  //       // 弹窗关闭时立即刷新草稿数量
  //       // fetchDraftCount();
  //     },
  //     // showModal: false,
  //   }).catch(() => {
  //     // 用户取消或关闭弹窗（兜底处理，onCancel 已经处理了刷新逻辑）
  //   });
  // };

  /**
   * 切换编辑模式 - 缩小到窗口模式
   */
  const handleToggleMode = () => {
    console.log('GlobalMailEditorModal handleToggleMode called', { editorMode, editorRef: !!editorRef.current });

    if (editorRef.current) {
      // 保存当前内容到store
      const currentContent = editorRef.current.getContent();
      const currentAttachments = editorRef.current.getAttachments();

      setContent(currentContent);
      setAttachments(currentAttachments);
      setSubject(subject);
    }

    // 切换到窗口模式
    setEditorMode('window');
    setDialogVisible(false);
    onCancel?.();
  };

  const handleAttachmentsClick = () => {
    console.log('handleAttachmentsClick');
    const el = document.querySelector('.scrollBox');
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-[#00000080]"
        // onClick={handleClose}
      />

      {/* 弹窗内容 */}
      <div className="relative w-[90vw] h-[85vh] min-w-[864px] min-h-[648px] max-w-[95vw] max-h-[95vh] bg-white rounded-[8px] shadow-2xl flex flex-col">
        {/* 编辑器内容区域 - 包含底部工具栏 */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* 头部区域 */}
          <div className="flex-shrink-0 pt-3 pl-1 pr-4 gap-0">
            <div className="flex items-center gap-2 relative z-5">
              <div className="flex-1">
                <SubjectInput
                  value={subject}
                  onChange={setSubject}
                  maxLength={256}
                  placeholder="Please enter the Subject"
                />
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    onClick={handleAttachmentsClick}
                    hidden={attachments.length === 0}
                    className="flex rounded-[8px] p-2 items-center gap-1 cursor-pointer hover:bg-[#E5E5E5]"
                  >
                    <Paperclip size={16} />
                    <span className="text-xs text-500 font-medium text-[#262626] ">({attachments.length || 0})</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="z-[101]">
                  <p>Attachments</p>
                </TooltipContent>
              </Tooltip>

              {/* <Tooltip>
                <TooltipTrigger asChild> */}
              {/* <div
                onClick={handleDraftsClick}
                className="flex rounded-[8px] p-2 items-center gap-1 cursor-pointer hover:bg-[#E5E5E5]"
              >
                <FileText size={16} />
                {draftCount > 0 && <span className="text-xs text-500 font-medium text-[#262626]">({draftCount})</span>}
              </div> */}
              {/* </TooltipTrigger>
                <TooltipContent>
                  <p>Drafts</p>
                </TooltipContent>
              </Tooltip> */}

              {/* <Button
                variant="ghost"
                id="contactsdetails_setting_click"
                onClick={() => {
                  mixpanel.track('contactsdetails_setting_click');
                  navigate(PagePath.home);
                  onCancel?.();
                }}
                size="sm"
                className="h-8 w-8 p-0 flex-shrink-0 hover:bg-[#E5E5E5]"
                title="Settings"
              >
                <Settings size={16} />
              </Button> */}
              <div className="border-l border-gray-300 h-4.5" hidden={attachments.length === 0}></div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleMode}
                className="h-8 w-8 p-0 flex-shrink-0 hover:bg-[#E5E5E5]"
                title="Minimize"
              >
                <Minimize2 size={16} />
              </Button>
              {/* <div className="border-l border-gray-300 h-4.5"></div> */}
              {/* <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-8 w-8 p-0 flex-shrink-0"
                title="Close"
              >
                <X size={16} />
              </Button> */}
            </div>
          </div>

          {/* 编辑器区域 */}
          <div className="flex-1 flex flex-col h-[calc(100%-48px)] pb-4">
            <Suspense fallback={<PageLoading />}>
              <LazyMailEditor
                ref={editorRef}
                uniqueClass="mail-editor-global-modal"
                attachedStickyBottom={true}
                showBottomToolbar={true}
                onEditCreated={onEditCreated}
                onContentEditingChange={notifyContentEditing}
                customToolbarPlugin={<EditorPlugin editorRef={editorRef} dialogSubject={subject} onCancel={onCancel} />}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalMailEditorModal;
